'use client';

import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import logMultiDayRouteTagClicked from '@alltrails/amplitude/events/logMultiDayRouteTagClicked';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import TableRow from '@alltrails/denali/components/TableRow';
import Backpacking from '@alltrails/denali/icons/Backpacking';
import type TrailDetails from '@alltrails/types/TrailDetails';
import styles from './ParentTrails.module.scss';

const ParentTrailsModal = dynamic(() => import('./ParentTrailsModal'), { ssr: false });

type ParentTrailsProps = {
  trails: NonNullable<TrailDetails['parentTrails']>;
  trailId: number;
};

// https://www.figma.com/design/B7oyzdXDXbRUGayrIwJnRp/Adv-Planning-2026--Handoff-?node-id=2788-37034&t=QdvY1ccj4HKq2Qkj-4
const ParentTrails = ({ trails, trailId }: ParentTrailsProps) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalHasOpened, setModalHasOpened] = useState(false);

  const trailIds = useMemo(() => trails.map(trail => trail.id), [trails]);

  if (trails.length === 0) {
    return null;
  }

  if (trails.length === 1) {
    const { name, slug, id } = trails[0];
    const onClick = () => {
      logTrailCardClicked({
        card_location: 'web trail page',
        detailed_card_location: 'trail details part of multi day route',
        trail_id: String(id)
      });
      logMultiDayRouteTagClicked({
        destination_trail_id: id,
        trail_id: trailId
      });
    };
    return (
      <a className={styles.container} href={wrapUrlSafe(`/trail/${slug}`, languageRegionCode)} onClick={onClick} target="_blank">
        <TableRow
          label={intl.formatMessage({ defaultMessage: 'Part of a multi-day route' })}
          description={name}
          leftContent={
            <div className={styles.iconContainer}>
              <Backpacking color="--color-text-secondary" />
            </div>
          }
        />
      </a>
    );
  }

  return (
    <>
      <button
        className={styles.container}
        onClick={() => {
          setModalHasOpened(true);
          setIsModalOpen(true);
        }}
      >
        <TableRow
          label={intl.formatMessage({ defaultMessage: `Part of {count} multi-day routes` }, { count: trails.length })}
          description={intl.formatMessage(
            // needs to use plural because different languages will translate "more" depending on the number of trails
            { defaultMessage: `{trailName} +{count, plural, one {# more} other {# more}}` },
            { trailName: trails[0].name, count: trails.length - 1 }
          )}
          leftContent={
            <div className={styles.iconContainer}>
              <Backpacking color="--color-text-secondary" />
            </div>
          }
        />
      </button>
      {modalHasOpened && (
        <ParentTrailsModal isOpen={isModalOpen} onCloseRequest={() => setIsModalOpen(false)} currentTrailId={trailId} trailIds={trailIds} />
      )}
    </>
  );
};

export default ParentTrails;
